import React, { useState, useEffect } from "react";
import { getNextMeetings, getNextMeetingsAsync, onMeetingsCacheUpdate } from "../utils/dateHelpers";

const MeetingDatePicker = ({
  value,
  values = [],
  onChange,
  limit = 8,
  multi = false,
  showDescription = true,
}) => {
  const [meetings, setMeetings] = useState(() => getNextMeetings(limit));

  // Charger les réunions depuis Firebase (avec dates exclues)
  useEffect(() => {
    const loadMeetings = async () => {
      try {
        const asyncMeetings = await getNextMeetingsAsync(limit);
        setMeetings(asyncMeetings);
      } catch (error) {
        console.warn('⚠️ [MeetingDatePicker] Erreur chargement async meetings:', error);
        // En cas d'erreur, garder les réunions synchrones
      }
    };
    
    loadMeetings();
    
    // Écouter les mises à jour du cache réactif
    const unsubscribe = onMeetingsCacheUpdate(async () => {
      console.log('🔄 [MeetingDatePicker] Cache mis à jour, rechargement...');
      await loadMeetings();
    });
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [limit]);

  const handleClick = (m) => {
    if (!onChange) return;
    onChange(m);
  };

  const isSelected = (m) => {
    if (multi) {
      return Array.isArray(values) && values.includes(m.meetingAt);
    }
    return value === m.meetingAt;
  };

  // Fonction pour obtenir le style de couleur d'un événement
  const getEventStyle = (meeting) => {
    if (!meeting.isSpecial || !meeting.color) {
      return {};
    }

    const colorMap = {
      'orange': {
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
        borderColor: '#f97316',
        color: '#9a3412',
      },
      'green': {
        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        borderColor: '#10b981',
        color: '#065f46',
      },
      'blue': {
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        borderColor: '#3b82f6',
        color: '#1e40af',
      },
      'red': {
        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        borderColor: '#ef4444',
        color: '#991b1b',
      },
      'purple': {
        background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
        borderColor: '#a855f7',
        color: '#6b21a8',
      },
    };

    const colorStyle = colorMap[meeting.color] || colorMap['orange'];
    
    if (isSelected(meeting)) {
      // Style pour l'état sélectionné
      const selectedMap = {
        'orange': {
          background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
          borderColor: '#ea580c',
          color: 'white',
        },
        'green': {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderColor: '#059669',
          color: 'white',
        },
        'blue': {
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          borderColor: '#2563eb',
          color: 'white',
        },
        'red': {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          borderColor: '#dc2626',
          color: 'white',
        },
        'purple': {
          background: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
          borderColor: '#9333ea',
          color: 'white',
        },
      };
      return selectedMap[meeting.color] || selectedMap['orange'];
    }

    return colorStyle;
  };

  return (
    <div className="meeting-date-picker">
      {meetings.map((m) => {
        const eventStyle = m.isSpecial && m.color ? getEventStyle(m) : {};
        
        // Si c'est un événement spécial avec une couleur personnalisée, ne pas utiliser les classes CSS qui forcent la couleur
        // Utiliser uniquement le style inline pour avoir le contrôle total
        const baseClassName = m.isSpecial && m.color 
          ? `date-option ${isSelected(m) ? "selected" : ""}` 
          : `date-option ${isSelected(m) ? "selected" : ""} ${m.isSpecial ? (m.specialType === 'week' ? 'special-week' : m.specialType === 'event' ? 'special-event' : 'special') : ""}`;
        
        // Log pour déboguer
        if (m.isSpecial && m.color) {
          console.log('🎨 [MeetingDatePicker] Événement spécial:', {
            label: m.label,
            color: m.color,
            style: eventStyle,
            hasStyle: Object.keys(eventStyle).length > 0
          });
        }
        
        // Style final : combiner le style de base avec le style de l'événement
        // IMPORTANT: Si c'est un événement spécial avec couleur, FORCER le style inline
        const finalStyle = m.isSpecial && m.color ? {
          ...eventStyle,
          fontWeight: 600,
          borderWidth: '2px',
          borderStyle: 'solid',
          // Forcer l'override des styles CSS
          background: eventStyle.background || undefined,
          borderColor: eventStyle.borderColor || undefined,
          color: eventStyle.color || undefined,
        } : {};
        
        // Log détaillé pour déboguer
        if (m.isSpecial && m.color) {
          console.log('🎨 [MeetingDatePicker] Style final appliqué:', {
            label: m.label,
            color: m.color,
            finalStyle,
            hasBackground: !!finalStyle.background,
            hasBorderColor: !!finalStyle.borderColor,
            className: baseClassName
          });
        }
        
        return (
          <div
            key={m.meetingAt}
            className={baseClassName}
            style={finalStyle}
            onClick={() => handleClick(m)}
          >
            <span>{showDescription ? m.label : (m.labelSimple || m.label)}</span>
          </div>
        );
      })}
    </div>
  );
};

export default MeetingDatePicker;
